import { DataSource } from '@angular/cdk/collections';
import { signal } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Column } from '@qtek/shared/models';
import {
  BehaviorSubject,
  isObservable,
  map,
  Observable,
  Subscription,
  tap,
} from 'rxjs';
import {
  FilterAdapter,
  FilterServerSideAdapter,
  GroupByAdapter,
  GroupByMetaAdapter,
  HistoryAdapter,
  PaginatorAdapter,
  QtControlAdapter,
  QtControlRegistry,
  QtDataSourceContext,
  SearchAdapter,
  SortAdapter,
} from './adapters';
import { PIPE_ORDER } from './adapters/qt-control-adapter';
import {
  MetadataHistoryComponent,
  PsGroupByComponent,
  QtFilterComponent,
} from './components';
import { QtFilterDirective, QtSearchDirective } from './directives';
import { ExtendDataTable, QtGroupBy, SearchPredicate } from './models';
import {
  defaultSearchPredicate,
  isExtendedDataTable,
  MetaControls,
  QtRouterSerializer,
} from './utils';
import { valueAccessor } from './value-accessor';

export type LegacyControlsBag<T> = {
  paginator?: MatPaginator;
  sort?: MatSort;
  search?: QtSearchDirective;
  /** @deprecated use filterComponent */
  filter?: QtFilterDirective;
  filterComponent?: QtFilterComponent;
  /**
   * The table filters server-side (e.g. a report query), so register a
   * persist-only FilterServerSideAdapter that saves/restores the filter without
   * adding client-side filtering to the pipeline. Defaults to the client-side
   * FilterAdapter.
   */
  filterServerSide?: boolean;
  groupBy?: QtGroupBy<T>;
  groupByComponent?: PsGroupByComponent;
  metaControls?: MetaControls;
  historyControl?: MetadataHistoryComponent;
};

/**
 * Produce data stream from provided data and connected controls.
 *
 * ```typescript
 * const dataSource = new QtDataSource<User>([]);
 *
 * dataSource.setControls({ paginator, sort });
 *
 * dataSource.connect().subscribe(console.log);
 * dataSource.disconnect();
 * ```
 */
export class QtDataSource<T = any> extends DataSource<T> {
  readonly registry = new QtControlRegistry<T>();

  private _metaControls: MetaControls | null = null;

  get metaControls(): MetaControls | null {
    return this._metaControls;
  }

  /** @deprecated Read via registry adapters or use setControls(). */
  get paginator(): MatPaginator | null {
    return (
      (this.registry.get('paginator') as PaginatorAdapter | undefined)
        ?.paginator ?? null
    );
  }

  /** @deprecated Read via registry adapters or use setControls(). */
  get sort(): MatSort | null {
    return (
      (this.registry.get('sort') as SortAdapter | undefined)?.matSort ?? null
    );
  }

  /** @deprecated Read via registry adapters or use setControls(). */
  get search(): QtSearchDirective | null {
    return (
      (this.registry.get('search') as SearchAdapter | undefined)?.directive ??
      null
    );
  }

  /** @deprecated Use filterComponent. */
  get filter(): QtFilterDirective | null {
    return (
      (this.registry.get('psFilters') as FilterAdapter | undefined)
        ?.filterComponent?.filter ?? null
    );
  }

  /** @deprecated Read via registry adapters or use setControls(). */
  get filterComponent(): QtFilterComponent | null {
    return (
      (this.registry.get('psFilters') as FilterAdapter | undefined)
        ?.filterComponent ?? null
    );
  }

  /** @deprecated Read via registry adapters or use setControls(). */
  get historyControl(): MetadataHistoryComponent | null {
    return (
      (this.registry.get('history') as HistoryAdapter | undefined)?.component ??
      null
    );
  }

  columns$ = new BehaviorSubject<Column[]>([]);
  groupingColumns$ = this.columns$
    .asObservable()
    .pipe(map(columns => columns.filter(column => column.grouping)));

  /**
   * Determine whether row should be filtered for given query.
   * Override to use custom predicate.
   */
  searchPredicate: SearchPredicate = defaultSearchPredicate;

  valueAccessorFn: (row: T, path: string) => unknown = (
    row: T,
    path: string
  ) => {
    return valueAccessor<T>(row, path);
  };

  /** Optional separate value accessor for sorting. Falls back to valueAccessorFn if not set. */
  sortValueAccessorFn: ((row: T, path: string) => unknown) | null = null;

  get data(): T[] {
    return this._data.value;
  }
  set data(value: T[] | Observable<T[]>) {
    this.setData(value);
  }

  _data = new BehaviorSubject<T[]>([]);

  groups = signal<Set<string>>(new Set());

  /** Stream emitting length of filtered data. */
  readonly length = new BehaviorSubject<number | null>(null);

  /** Stream emitting render data to the table. */
  public readonly renderData = new BehaviorSubject<T[]>([]);

  private renderChangesSubscription: Subscription;

  /** Hold subscription to data observable. */
  private dataSubscription: Subscription | null = null;

  groupedById = signal<string | undefined>(undefined);

  constructor(
    initialData: T[] | Observable<T[]>,
    public serializer?: QtRouterSerializer
  ) {
    super();

    this.setData(initialData);
    this.updateChangeSubscription();
  }

  /** Register any adapter; rebuilds pipeline. Chainable. */
  registerControl(adapter: QtControlAdapter): this {
    adapter.setContext?.(this as QtDataSourceContext<T>);
    this.registry.register(adapter);
    this.updateChangeSubscription();
    return this;
  }

  /**
   * Returns observable with latest data after all transformations.
   */
  connect(): Observable<T[]> {
    return this.renderData.asObservable();
  }

  /**
   * Unsubscribe from streams and release memory.
   */
  disconnect() {
    this.renderChangesSubscription?.unsubscribe();

    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }

    if (this.serializer) {
      this.serializer.disconnect();
    }

    if (this._metaControls) {
      this._metaControls.disconnect();
    }

    this.registry.destroy();
  }

  updateChangeSubscription() {
    this.renderChangesSubscription?.unsubscribe();

    const transforms = this.registry.getDataTransforms();
    const preTransforms = transforms.filter(
      a => a.pipeOrder <= PIPE_ORDER.SORT
    );
    const postTransforms = transforms.filter(
      a => a.pipeOrder > PIPE_ORDER.SORT
    );

    const pre$ = preTransforms.reduce(
      (obs$, a) => obs$.pipe(a.operator),
      this._data.asObservable() as Observable<T[]>
    );

    const withLength$ = pre$.pipe(tap(data => this.length.next(data.length)));

    const final$ = postTransforms.reduce(
      (obs$, a) => obs$.pipe(a.operator),
      withLength$
    );

    this.renderChangesSubscription = final$.subscribe(data => {
      const groupedById = this.setGroups(data);
      this.groupedById.set(groupedById);
      this.renderData.next(data);
    });
  }

  /**
   * Backwards-compatible sugar. Creates adapters from legacy controls bag and registers them.
   * Safe to call multiple times — guards against duplicate registrations.
   * New code should call registerControl() directly.
   */
  setControls(controls: LegacyControlsBag<T>): void {
    if (controls.search && !this.registry.get('search')) {
      const a = new SearchAdapter(controls.search);
      a.setContext?.(this as QtDataSourceContext<T>);
      this.registry.register(a);
    }
    if (controls.sort && !this.registry.get('sort')) {
      const a = new SortAdapter(controls.sort);
      a.setContext?.(this as QtDataSourceContext<T>);
      this.registry.register(a);
    }
    // A remounted table brings a new paginator; presence alone keeps feeding the discarded one.
    const boundPaginator = (
      this.registry.get('paginator') as PaginatorAdapter | undefined
    )?.paginator;
    if (controls.paginator && controls.paginator !== boundPaginator) {
      this.registry.register(new PaginatorAdapter(controls.paginator));
    }
    if (controls.filterComponent && !this.registry.get('psFilters')) {
      const a: QtControlAdapter = controls.filterServerSide
        ? new FilterServerSideAdapter(controls.filterComponent)
        : new FilterAdapter(controls.filterComponent);
      a.setContext?.(this as QtDataSourceContext<T>);
      this.registry.register(a);
    }
    if (controls.groupByComponent && !this.registry.get('groupBy')) {
      const a = new GroupByMetaAdapter(controls.groupByComponent);
      a.setContext?.(this as QtDataSourceContext<T>);
      this.registry.register(a);
    }
    if (controls.groupBy && !this.registry.get('groupByStatic')) {
      const a = new GroupByAdapter(controls.groupBy);
      a.setContext?.(this as QtDataSourceContext<T>);
      this.registry.register(a);
    }
    if (controls.historyControl && !this.registry.get('history')) {
      this.registry.register(new HistoryAdapter(controls.historyControl));
    }
    if (controls.metaControls && !this._metaControls) {
      this._metaControls = controls.metaControls;
      this._metaControls.setRegistry(this.registry);
    }

    if (this.serializer) {
      this.serializer.setControls(controls, true);
    }

    this.updateChangeSubscription();
  }

  setMetaControls(mc: MetaControls): this {
    if (!this._metaControls) {
      this._metaControls = mc;
      mc.setRegistry(this.registry);
    }
    return this;
  }

  setValueAccessor(valueAccessorFn: (row: T, path: string) => unknown) {
    this.valueAccessorFn = valueAccessorFn;
    this.updateChangeSubscription();
  }

  /**
   * Pass data from observable or array to data subject.
   * Unsubscribes from previous data source.
   */
  private setData(data: T[] | Observable<T[]>) {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
      this.dataSubscription = null;
    }

    if (isObservable(data)) {
      this.dataSubscription = data.subscribe(d => this._data.next(d));
    } else {
      this._data.next(data);
    }
  }

  private setGroups(data: T[]): string | null {
    const groupByValues = data
      .filter((item: T): item is T & ExtendDataTable =>
        isExtendedDataTable(item)
      )
      .map(item => item.__groupBy);
    this.groups.set(new Set(groupByValues));

    if (groupByValues.length === 0) {
      return null;
    }

    return (
      data.find(
        (item: any): item is T & ExtendDataTable =>
          typeof item.__groupById === 'string'
      )?.__groupById ?? null
    );
  }
}
